import React from "react";

const DEFAULT_GRADE_LEVEL_OPTIONS = [
  { value: "junior", label: "Junior Infants" },
  { value: "senior", label: "Senior Infants" },
];

/**
 * Combined picker: class dropdown + student checkbox list + Generate button.
 * Pure controlled component - all state lives in the parent panel.
 */
function ClassStudentPicker({
  classes,
  classesLoading,
  classesError,
  selectedGradeLevel,
  onGradeLevelChange,
  gradeLevelOptions = DEFAULT_GRADE_LEVEL_OPTIONS,
  gradeLevelPrompt = "Junior or Senior Infants",
  selectedClassId,
  onClassChange,
  academicYearOptions = [],
  selectedAcademicYear = "",
  onAcademicYearChange,
  screeningPointOptions = [],
  screeningPointRequired = false,
  screeningPointLoading = false,
  screeningPointError = "",
  selectedScreeningPoint = "",
  onScreeningPointChange,
  students,
  studentsLoading,
  studentsError,
  selectedStudentIds,
  unavailableStudentIds = [],
  onStudentToggle,
  onSelectAll,
  disclaimerAccepted,
  onDisclaimerAcceptedChange,
  onGenerate,
  generating,
  viewOnly = false,
  academicYearLabel = "the selected school year",
  disclaimerText = "I understand these reports are AI-generated and should be reviewed before sharing with parents.",
}) {
  const requiresAcademicYear = academicYearOptions.length > 0;
  const requiresScreeningPoint =
    screeningPointRequired || screeningPointOptions.length > 0;
  const screeningPointReady = !requiresScreeningPoint || selectedScreeningPoint;
  const gradeStepNumber = requiresAcademicYear ? 2 : 1;
  const classStepNumber = requiresAcademicYear ? 3 : 2;
  const screeningPointStepNumber = requiresAcademicYear ? 4 : 3;
  const studentStepNumber = requiresAcademicYear
    ? requiresScreeningPoint
      ? 5
      : 4
    : requiresScreeningPoint
      ? 4
      : 3;
  const unavailableStudentIdSet = new Set(unavailableStudentIds);
  const selectableStudents = students.filter(
    (student) => !unavailableStudentIdSet.has(student.id),
  );
  const allSelected =
    selectableStudents.length > 0 &&
    selectedStudentIds.length === selectableStudents.length;
  const someSelected = selectedStudentIds.length > 0;
  const canGenerate = someSelected && disclaimerAccepted && !generating;
  const generateButtonLabel = generating
    ? "Generating..."
    : someSelected
      ? `Generate ${selectedStudentIds.length} report${selectedStudentIds.length === 1 ? "" : "s"}`
      : "Pick at least one student";
  const actionHint =
    someSelected && !generating
      ? disclaimerAccepted
        ? `Roughly ${selectedStudentIds.length * 2}s total.`
        : "Please agree to the AI-generated report disclaimer."
      : "";

  return (
    <div className="eoy-picker">
      {requiresAcademicYear && (
        <div className="eoy-field">
          <label htmlFor="eoy-academic-year-select" className="eoy-field-label">
            Step 1 - Select a school year
          </label>
          <select
            id="eoy-academic-year-select"
            className="eoy-select"
            value={selectedAcademicYear}
            onChange={(e) => onAcademicYearChange?.(e.target.value)}
            disabled={generating}
          >
            {academicYearOptions.map((option) => (
              <option key={option.storage} value={option.storage}>
                {option.display}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="eoy-field">
        <label htmlFor="eoy-grade-select" className="eoy-field-label">
          Step {gradeStepNumber} - Select {gradeLevelPrompt}
        </label>
        <select
          id="eoy-grade-select"
          className="eoy-select"
          value={selectedGradeLevel}
          onChange={(e) => onGradeLevelChange(e.target.value)}
          disabled={generating}
        >
          <option value="">-- Choose {gradeLevelPrompt} --</option>
          {gradeLevelOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {/* Class dropdown ---------------------------------------------------- */}
      <div className="eoy-field">
        <label htmlFor="eoy-class-select" className="eoy-field-label">
          Step {classStepNumber} - Select a class
        </label>
        {!selectedGradeLevel ? (
          <div className="eoy-muted">
            Choose {gradeLevelPrompt} first.
          </div>
        ) : classesLoading ? (
          <div className="eoy-muted">Loading your classes...</div>
        ) : classesError ? (
          <div className="eoy-error-inline">{classesError}</div>
        ) : classes.length === 0 ? (
          <div className="eoy-muted">
            No matching classes found for {academicYearLabel}. If you believe
            this is an error, please contact your school administrator.
          </div>
        ) : (
          <select
            id="eoy-class-select"
            className="eoy-select"
            value={selectedClassId}
            onChange={(e) => onClassChange(e.target.value)}
            disabled={generating}
          >
            <option value="">-- Choose a class --</option>
            {classes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
                {c.grade_level ? ` (${c.grade_level})` : ""}
              </option>
            ))}
          </select>
        )}
      </div>

      {selectedClassId && requiresScreeningPoint && (
        <div className="eoy-field">
          <label
            htmlFor="eoy-screening-point-select"
            className="eoy-field-label"
          >
            Step {screeningPointStepNumber} - Select a screening point
          </label>
          {screeningPointLoading ? (
            <div className="eoy-muted">Finding the current screening point...</div>
          ) : screeningPointError ? (
            <div className="eoy-error-inline">{screeningPointError}</div>
          ) : screeningPointOptions.length === 0 ? (
            <div className="eoy-muted">
              No open or completed screening points are available for this class.
            </div>
          ) : (
            <select
              id="eoy-screening-point-select"
              className="eoy-select"
              value={selectedScreeningPoint}
              onChange={(e) => onScreeningPointChange?.(e.target.value)}
              disabled={generating}
            >
              <option value="">-- Choose a screening point --</option>
              {screeningPointOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      {/* Student list ------------------------------------------------------ */}
      {selectedClassId && screeningPointReady && viewOnly && (
        <div className="eoy-field">
          <div className="eoy-muted">
            Showing existing {academicYearLabel} reports for every pupil in this
            class. Reports cannot be created or changed here.
          </div>
        </div>
      )}

      {selectedClassId && screeningPointReady && !viewOnly && (
        <div className="eoy-field">
          <label className="eoy-field-label">
            Step {studentStepNumber} - Pick the students
          </label>

          {studentsLoading && (
            <div className="eoy-muted">Loading students...</div>
          )}

          {studentsError && (
            <div className="eoy-error-inline">{studentsError}</div>
          )}

          {!studentsLoading && !studentsError && students.length === 0 && (
            <div className="eoy-muted">No students found in this class.</div>
          )}

          {!studentsLoading && students.length > 0 && (
            <>
              <div className="eoy-select-all-row">
                <label className="eoy-checkbox-label">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={onSelectAll}
                    disabled={generating || selectableStudents.length === 0}
                  />
                  <span>
                    {allSelected
                      ? `Deselect all (${selectableStudents.length})`
                      : `Reports not already run, available for creation (${selectableStudents.length})`}
                  </span>
                </label>
                <span className="eoy-selected-count">
                  {selectedStudentIds.length} selected
                </span>
              </div>

              <div className="eoy-student-grid">
                {students.map((student) => {
                  const displayName =
                    [student.firstname, student.lastname]
                      .filter(Boolean)
                      .join(" ") ||
                    student.name ||
                    student.id;

                  const checked = selectedStudentIds.includes(student.id);
                  const unavailable = unavailableStudentIdSet.has(student.id);
                  return (
                    <label
                      key={student.id}
                      className={`eoy-student-chip ${checked ? "selected" : ""} ${
                        unavailable ? "disabled" : ""
                      }`}
                      data-tooltip={
                        unavailable ? "Report Already Generated" : undefined
                      }
                      aria-label={
                        unavailable
                          ? `${displayName} - Report Already Generated`
                          : displayName
                      }
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => onStudentToggle(student.id)}
                        disabled={generating || unavailable}
                      />
                      <span>{displayName}</span>
                    </label>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {/* Generate button --------------------------------------------------- */}
      {selectedClassId &&
        screeningPointReady &&
        !viewOnly &&
        selectableStudents.length > 0 && (
          <>
            <label className="eoy-disclaimer-check">
              <input
                type="checkbox"
                checked={disclaimerAccepted}
                onChange={(e) => onDisclaimerAcceptedChange(e.target.checked)}
                disabled={generating}
              />
              <span>{disclaimerText}</span>
            </label>
            <div className="eoy-action-row">
              <button
                type="button"
                className="eoy-primary-btn"
                onClick={onGenerate}
                disabled={!canGenerate}
              >
                {generateButtonLabel}
              </button>
              {actionHint && (
                <span className="eoy-action-hint">{actionHint}</span>
              )}
            </div>
          </>
        )}
    </div>
  );
}

export default ClassStudentPicker;
